  // Stable positional layout of a charge row tuple. Origin (port_fees_from) and destination
  // (port_fees_to) share this shape. Index 8 (isEstimate) is additive — readers of 0..7 are
  // unaffected, and both variants (ECU rows carry calcCode@6/qty@7; SpotOn per-equipment rows
  // carry the equipment code @6) leave 8 free, so the flag never collides.
  export const CHARGE_ROW = Object.freeze({
    IDEN: 0,
    DESCRIPTION: 1,
    UNIT_COST: 2,
    UNIT_CODE: 3,
    MINIMUM_PRICE: 4,
    ID: 5,
    CALCULATION_CODE: 6,
    QUANTITY: 7,
    IS_ESTIMATE: 8,
  });

  // True when a charge line the backend produced is flagged as an estimate.
  const chargeLineIsEstimate = (charge) => charge?.isEstimate === true;

  // True when any row in a formatted charge array is an estimate (index 8).
  export const isEstimatedCharges = (rows = []) =>
    Array.isArray(rows) &&
    rows.some((row) => Array.isArray(row) && row[CHARGE_ROW.IS_ESTIMATE] === true);

  // Convert to expected format
  export const convertToFormat = (charges) => {
    if (!Array.isArray(charges)) {
      return [];
    }

    return charges.map(charge => {
      // Never render a blank label — fall back to the charge code (e.g. an estimated row that came
      // back with an empty description). Belt-and-suspenders alongside the backend fixes.
      let description = charge.description || charge.chargeIden || 'Port charge';
      if ( //charge.id === 60 &&
        charge.comments) {
        description = `${description} - ${charge.comments}`;
      }

      return [
        charge.chargeIden,
        description,
        charge.unitCost,
        charge.unitCode,
        charge.minimumPrice,
        charge.id,
        charge.calculationCode,
        charge.quantity,
        chargeLineIsEstimate(charge),
      ];
    });
  };


/**
 * @memberof module:util
 * @function
 * @name getDestinationCharges
 * @description Get merged destination charges from default charges and selling rate charges,
 * filtered by excluded charges for web users
 * 
 * @param {Array} defaultDestinationCharges - Default destination charges from backend
 * @param {Array} sellingRateDestCharges - Destination charges from selling rate
 * @param {Array} excludedCharges - Excluded charges for web user
 * @param {Object} options - Additional options
 * @param {number} options.originPortId - Origin port ID for OVERSEAS charge logic
 * @param {boolean} options.isSpotOn - Whether rate is spotOn
 * @param {string} options.rateType - Rate type (FCL/LCL)
 * @returns {Array} Merged and filtered destination charges in expected format
 */
export const getDestinationCharges = (
  defaultDestinationCharges = [],
  sellingRateDestCharges = [],
  excludedCharges = [],
  rate = {}
) => {
  // const { originPortId, isSpotOn, rateType } = options;
  // console.log("getDestinationCharges: ",defaultDestinationCharges,
  //   "\nsellingRateDestCharges: ", sellingRateDestCharges,
  //   "\nexcludedCharges: ", excludedCharges,
  //   "\nrate: ", rate
  // )
  // Filter out excluded charges from defaultDestinationCharges
  let filteredDefaultCharges = defaultDestinationCharges || [];
  if (excludedCharges && excludedCharges.length > 0 && filteredDefaultCharges.length > 0) {
    const excludedChargeIds = excludedCharges.map(
      (charge) => charge?.id || charge?.chargeId
    );
    filteredDefaultCharges = filteredDefaultCharges.filter(
      (charge) => !excludedChargeIds.includes(charge.id)
    );
  }

  // Merge default and selling rate charges
  const destinationSectionCharges = [
    ...filteredDefaultCharges,
    ...(sellingRateDestCharges || [])
  ];



  // Find and separate OVERSEAS charge
  const overseasCharge = destinationSectionCharges.find(
    charge => charge.chargeIden === 'OVERSEAS'
  );
  
  // Exclude OVERSEAS charge initially
  const filteredCharges = destinationSectionCharges.filter(
    charge => charge.chargeIden !== 'OVERSEAS'
  );
  
  let destinationCharges = convertToFormat(filteredCharges);
  // console.log("destinationCharges: ",destinationCharges);
  const isSpotOn = Boolean(rate?.spotRate ?? rate?.spot_rate)
  const originLocationFromId = rate?.location_from?.id;
  const rateType = rate?.rate_type;
  // Add OVERSEAS charge conditionally for FCL/LCL rates based on origin port ID and spotOn check
  if (originLocationFromId === 653441 && !isSpotOn && overseasCharge && (rateType === 'FCL' || rateType === 'LCL')) {
    const overseasChargeFormatted = [
      overseasCharge.chargeIden,
      overseasCharge.description,
      overseasCharge.unitCost,
      overseasCharge.unitCode,
      overseasCharge?.minimumPrice || overseasCharge?.unitCost,
      overseasCharge.id,
    ];
    destinationCharges.push(overseasChargeFormatted);
  }

  return {
    destinationCharges,
    overseasCharge,
    isEstimated: isEstimatedCharges(destinationCharges),
  };
};

export const getOriginCharges = (sellingRateOriginCharges = [], rate = {}) => {
  // console.log("getOriginCharges...",
  //   "\n sellingRateOriginCharges: ",sellingRateOriginCharges,
  //   "\n rate: ", rate
  // );

  // Honor real/estimated origin charges for ANY mode when present (FCL included) — the port-charges
  // cache now fills origin extras for FCL too. Only fall back to the "will advise" placeholder when
  // no origin charges were provided. (Was previously gated to LCL, which dropped FCL cache hits.)
  if(Array.isArray(sellingRateOriginCharges) && sellingRateOriginCharges.length > 0){
    let originCharges = convertToFormat(sellingRateOriginCharges);
    // console.log("originCharges: ",originCharges);
    return originCharges;
  }else{
    return ([
      [
        "Variable Charge",
        "Variable Charge: Prime Freight will advise of the final origin charges after booking",
        "",
        // false,
        "EA", // unit_code (replaces multiply flag)
        "",
      ],
    ]);
  }
}
